// @ts-nocheck
import { MOCK } from "@/state/app-store";
import { UI } from "@/components/ui";
import { Icon } from "@/components/icons";
import { api } from "@/api/index";

const { $, $$, fmtNum, fmtMoney, fmtPercent, toast, confirmModal, hasPerm, statusBadge, trendArrow, statCard, channelChip, capabilityBadge, pageHeader, emptyState, primaryBtn, ghostBtn, stepIndicator, BUSINESS_COLORS, chartTooltip, chartXAxis, chartYAxis, chartLineSeries, chartBarSeries } = UI;

declare const echarts: any;
declare const Pages: Record<string, any>;
declare const PageHooks: { mounted?: ((path: string) => void) | null };
declare function navigate(path: string): void;
declare function getCurrentPath(): string;
declare function getCurrentPathWithQuery(): string;
declare function renderRoute(): void;

if (typeof window !== "undefined") {
  (window as any).Pages = (window as any).Pages || {};
}

const Pages = (window as any).Pages;

// ============================================================
// 游戏创建成功页 - 凭证页 + 下一步指引
// 关键信息：Game ID / Game Secret / 创建人 / 创建时间 / 下一步
// ============================================================

window.Pages = window.Pages || {};

Pages.gameCredential = function (gameId) {
  let cred = window.__newGameCredential;
  // 兼容预览面板 hash 跳转时可能整页刷新：从 sessionStorage 兜底恢复凭证
  if (!cred || cred.game?.id !== gameId) {
    try {
      const raw = sessionStorage.getItem(`easyshare:newGameCredential:${gameId}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        cred = { ...parsed, createdAt: new Date(parsed.createdAt) };
        window.__newGameCredential = cred;
      }
    } catch (e) {
      console.warn("Failed to restore new game credential", e);
    }
  }
  if (!cred || cred.game.id !== gameId) {
    return `
      ${UI.pageHeader({ title: "创建结果不可见" })}
      <div class="card p-10 text-center max-w-2xl mx-auto">
        <div class="w-14 h-14 mx-auto mb-3 rounded-full bg-warn-50 border border-warn-200 flex items-center justify-center">
          ${Icon.info({ size: 24, color: "#B45309" })}
        </div>
        <h3 class="text-base font-semibold text-ink-900">该创建结果页已失效</h3>
        <p class="text-sm text-ink-500 mt-2 max-w-md mx-auto">为了安全，Game Secret 仅在创建成功后展示一次。如未保存，请到游戏编辑页的 API 密钥模块重置。</p>
        <div class="mt-5 flex items-center justify-center gap-2">
          ${UI.ghostBtn("返回游戏列表", { onclick: "navigate('/dev/games')" })}
          ${UI.primaryBtn("打开游戏详情", { onclick: `navigate('/dev/games/${gameId}')` })}
        </div>
      </div>
    `;
  }

  const g = cred.game;
  const sec = cred.secret;
  const createdStr = cred.createdAt.toLocaleString("zh-CN", { hour12: false });
  const creator = (MOCK.USERS && MOCK.USERS.find(u => u.role === MOCK.SESSION.currentRole)) || { name: "李管理" };
  const nameZh = g.name_zh || g.name;
  const nameEn = g.name_en || g.code;
  const iconHtml = typeof g.icon === "string" && g.icon.startsWith("data:image")
    ? `<img src="${g.icon}" class="w-7 h-7 rounded-md object-cover border border-ink-200" alt="${g.name}" />`
    : `<span class="text-lg">${g.icon}</span>`;

  return `
    <div class="max-w-3xl mx-auto">

      <!-- 顶部成功标识 -->
      <div class="flex items-start gap-3 mb-5 mt-1">
        <div class="w-10 h-10 rounded-full bg-ok-50 border border-ok-200 flex items-center justify-center flex-shrink-0">
          <svg class="w-5 h-5 text-ok-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/></svg>
        </div>
        <div class="flex-1 min-w-0">
          <h1 class="text-xl font-semibold text-ink-900">游戏创建成功</h1>
          <div class="flex items-center gap-2 mt-1 text-sm text-ink-700">
            ${iconHtml}
            <span class="font-semibold">${nameZh}</span>
            <span class="text-ink-300">/</span>
            <span class="font-medium text-ink-600">${nameEn}</span>
            <span class="text-ink-300">·</span>
            <span class="text-ink-500">${g.category}</span>
          </div>
          <div class="flex items-center gap-3 mt-1 text-xs text-ink-500">
            <span class="flex items-center gap-1">
              ${Icon.users({ size: 11, color: "#94A3B8" })}
              <span>由 <strong class="text-ink-700">${creator.name}</strong> 创建</span>
            </span>
            <span class="text-ink-300">·</span>
            <span class="num">${createdStr}</span>
          </div>
        </div>
        <button onclick="downloadCredential()" class="px-3 py-2 text-xs border border-ink-200 rounded-md hover:bg-ink-50 text-ink-700 flex items-center gap-1.5 flex-shrink-0">
          ${Icon.download({ size: 13, color: "#475569" })}
          下载凭证
        </button>
      </div>

      <!-- ① Game ID -->
      <div class="card p-5 mb-3">
        <div class="flex items-center justify-between mb-2">
          <div class="flex items-center gap-2">
            <span class="text-xs font-semibold tracking-wider text-ink-500">GAME ID</span>
            <span class="badge bg-brand-50 text-brand-700">公开</span>
          </div>
          <button onclick="copyText('${g.id}', 'Game ID ')" class="flex items-center gap-1 text-xs text-brand-700 hover:text-brand-900 font-medium">
            ${Icon.copy({ size: 12, color: "#1E3A8A" })}
            <span>复制</span>
          </button>
        </div>
        <div class="px-3 py-3 bg-ink-50 border border-ink-200 rounded-md font-mono text-base font-semibold text-ink-900 select-all break-all">${g.id}</div>
        <p class="text-xs text-ink-500 mt-2">客户端 SDK 初始化、API 调用、数据归集使用，可在前端代码中明文使用。</p>
      </div>

      <!-- ② Game Secret -->
      <div class="card p-5 mb-3 border-l-4 border-l-warn-500">
        <div class="flex items-center justify-between mb-2">
          <div class="flex items-center gap-2 min-w-0">
            <span class="text-xs font-semibold tracking-wider text-ink-500">GAME SECRET</span>
            <span class="badge bg-warn-50 text-warn-700">机密</span>
            <span class="badge bg-danger-50 text-danger-700">仅展示一次</span>
          </div>
          <div class="flex items-center gap-3 flex-shrink-0">
            <button onclick="toggleCredSecret(this)" class="flex items-center gap-1 text-xs text-ink-600 hover:text-ink-900">
              <span class="eye flex items-center">${Icon.eyeOff({ size: 12, color: "#475569" })}</span>
              <span class="lbl">隐藏</span>
            </button>
            <button onclick="copyText(document.getElementById('cred-secret').dataset.real, 'Game Secret ')" class="flex items-center gap-1 text-xs text-warn-700 hover:text-warn-800 font-medium">
              ${Icon.copy({ size: 12, color: "#B45309" })}
              <span>复制</span>
            </button>
          </div>
        </div>
        <div id="cred-secret" data-real="${sec}" data-hidden="0" class="px-3 py-3 bg-warn-50 border border-warn-200 rounded-md font-mono text-base font-semibold text-ink-900 select-all break-all">${sec}</div>
        <p class="text-xs text-ink-500 mt-2">服务端与 SDK 后台通信的根密钥，<strong class="text-warn-700">仅服务端使用</strong>，切勿提交到代码仓库或前端暴露。</p>
        <div class="mt-2.5 flex items-start gap-2 px-3 py-2 bg-warn-50 border border-warn-200 rounded-md">
          <span class="text-sm flex-shrink-0">⚠️</span>
          <span class="text-xs text-warn-800">离开本页将不可再次查看，请立即复制保存到密钥管理系统（KMS / Vault / 1Password）。如丢失需在「游戏编辑 → API 密钥」中重置。</span>
        </div>
      </div>

      <!-- ③ 下一步指引 -->
      <div class="card p-5">
        <div class="flex items-center gap-2 mb-3">
          <span class="text-xs font-semibold tracking-wider text-ink-500">下一步</span>
          <span class="text-[11px] text-ink-400">完成接入流程</span>
        </div>
        <div class="grid grid-cols-3 gap-3">
          <button onclick="navigate('/dev/games/${g.id}/packages/create')" class="text-left p-3 border border-ink-200 rounded-md hover:border-brand-400 hover:bg-brand-50/50 group">
            <div class="flex items-center gap-2 mb-1.5">
              <span class="w-6 h-6 rounded-full bg-brand-50 border border-brand-200 text-brand-700 text-xs font-bold flex items-center justify-center">1</span>
              <div class="text-sm font-semibold text-ink-900 group-hover:text-brand-700">添加包体</div>
            </div>
            <div class="text-[11px] text-ink-500 leading-relaxed">为不同 Region × Platform 创建独立包体</div>
          </button>
          <button onclick="navigate('/dev/sdk-docs')" class="text-left p-3 border border-ink-200 rounded-md hover:border-brand-400 hover:bg-brand-50/50 group">
            <div class="flex items-center gap-2 mb-1.5">
              <span class="w-6 h-6 rounded-full bg-brand-50 border border-brand-200 text-brand-700 text-xs font-bold flex items-center justify-center">2</span>
              <div class="text-sm font-semibold text-ink-900 group-hover:text-brand-700">接入 SDK</div>
            </div>
            <div class="text-[11px] text-ink-500 leading-relaxed">使用 GameID 和 Game Secret 完成 SDK 初始化与服务端签名</div>
          </button>
          <button onclick="navigate('/ops/share-config')" class="text-left p-3 border border-ink-200 rounded-md hover:border-brand-400 hover:bg-brand-50/50 group">
            <div class="flex items-center gap-2 mb-1.5">
              <span class="w-6 h-6 rounded-full bg-brand-50 border border-brand-200 text-brand-700 text-xs font-bold flex items-center justify-center">3</span>
              <div class="text-sm font-semibold text-ink-900 group-hover:text-brand-700">设置分享</div>
            </div>
            <div class="text-[11px] text-ink-500 leading-relaxed">配置分享文案、图片、渠道与归因参数</div>
          </button>
        </div>
      </div>

      <!-- 底部 -->
      <div class="mt-5 flex items-center justify-end gap-2">
        ${UI.ghostBtn("返回游戏列表", { onclick: "navigate('/dev/games')" })}
        <button onclick="ackAndContinue('${g.id}')" class="px-4 py-2 bg-brand-900 hover:bg-brand-800 text-white text-sm font-medium rounded-md">
          完成并进入游戏详情
        </button>
      </div>
    </div>
  `;
};

// 显示/隐藏 Secret
window.toggleCredSecret = function (btn) {
  const wrap = document.getElementById("cred-secret");
  if (!wrap) return;
  const hidden = wrap.dataset.hidden === "1";
  if (hidden) {
    wrap.textContent = wrap.dataset.real;
    wrap.dataset.hidden = "0";
    btn.querySelector(".lbl").textContent = "隐藏";
    btn.querySelector(".eye").innerHTML = Icon.eyeOff({ size: 12, color: "#475569" });
  } else {
    wrap.textContent = "•".repeat(wrap.dataset.real.length);
    wrap.dataset.hidden = "1";
    btn.querySelector(".lbl").textContent = "显示";
    btn.querySelector(".eye").innerHTML = Icon.eye({ size: 12, color: "#475569" });
  }
};

// __window_exports__
// Expose helpers for cross-page / inline onclick compatibility
